import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Save, Loader2, UtensilsCrossed, Eye, EyeOff,
  ChevronDown, ExternalLink, Phone,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import ImageDropUpload from '../../components/admin/ImageDropUpload';
import { invalidateSettingsCache } from '../../lib/settingsCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'cruz-bay', label: 'DINING ~ CRUZ BAY' },
  { key: 'coral-bay', label: 'DINING ~ CORAL BAY' },
  { key: 'around-the-island', label: 'AROUND THE ISLAND' },
  { key: 'private-chefs', label: "PRIVATE CHEF'S / CATERING" },
];
const SECTIONS = [
  { key: 'upper', label: 'Upper section' },
  { key: 'lower', label: 'Lower section' },
];

const blankListing = () => ({
  name: '',
  phone: '',
  description: '',
  website_url: '',
  logo_url: '',
  category: CATEGORIES[0].key,
  section: 'upper',
  sort_order: 0,
  is_active: true,
});

const ListingEditor = ({ listing, onChange, onSave, onDelete, saving, isOpen, onToggle, sections }) => {
  const id = listing.id || 'new';
  const sectionLabel = listing.section === 'lower'
    ? (sections?.[listing.category]?.lower_title || 'Lower section')
    : (sections?.[listing.category]?.upper_title || 'Upper section');

  return (
    <Card data-testid={`food-listing-editor-${id}`}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={onToggle}
              className="p-1 rounded text-slate-500 hover:text-blue-700 hover:bg-slate-100 transition-colors"
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Collapse listing' : 'Expand listing'}
              data-testid={`food-accordion-${id}`}
            >
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <UtensilsCrossed className="w-5 h-5 text-blue-600 shrink-0" />
            <button type="button" onClick={onToggle} className="text-left min-w-0">
              <CardTitle className="text-base truncate">{listing.name || 'New Listing'}</CardTitle>
            </button>
            {listing.is_active ? (
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> Visible
              </span>
            ) : (
              <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <EyeOff className="w-3 h-3" /> Hidden
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange({ ...listing, is_active: !listing.is_active })}
              data-testid={`food-toggle-active-${id}`}
            >
              {listing.is_active ? 'Hide' : 'Show'}
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving} data-testid={`food-save-${id}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={onDelete}
              data-testid={`food-delete-${id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          {CATEGORIES.find((c) => c.key === listing.category)?.label} &middot; {sectionLabel}
          {listing.phone ? ` · ${listing.phone}` : ''} &middot; order {listing.sort_order ?? 0}
          {!isOpen && ' \u2014 click the chevron to edit'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4" hidden={!isOpen}>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <select
              value={listing.category}
              onChange={(e) => onChange({ ...listing, category: e.target.value })}
              className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              data-testid={`food-category-${id}`}
            >
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Section</Label>
            <select
              value={listing.section}
              onChange={(e) => onChange({ ...listing, section: e.target.value })}
              className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              data-testid={`food-section-${id}`}
            >
              {SECTIONS.map((sec) => (
                <option key={sec.key} value={sec.key}>
                  {sec.label}
                  {sections?.[listing.category]?.[`${sec.key}_title`]
                    ? ` — ${sections[listing.category][`${sec.key}_title`]}`
                    : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Name</Label>
            <Input
              value={listing.name}
              onChange={(e) => onChange({ ...listing, name: e.target.value })}
              placeholder="e.g. Miss Lucy&rsquo;s"
              className="mt-1"
              data-testid={`food-name-${id}`}
            />
          </div>
          <div>
            <Label>Order Number</Label>
            <Input
              type="number"
              value={listing.sort_order}
              onChange={(e) => onChange({ ...listing, sort_order: Number(e.target.value) || 0 })}
              className="mt-1"
              data-testid={`food-order-${id}`}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Phone</Label>
            <Input
              value={listing.phone}
              onChange={(e) => onChange({ ...listing, phone: e.target.value })}
              placeholder="340.693.5244"
              className="mt-1"
              data-testid={`food-phone-${id}`}
            />
          </div>
          <div>
            <Label className="flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Website</Label>
            <Input
              value={listing.website_url}
              onChange={(e) => onChange({ ...listing, website_url: e.target.value })}
              placeholder="misslucysrestaurant.com"
              className="mt-1"
              data-testid={`food-website-${id}`}
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank to hide the &ldquo;Visit Our Website&rdquo; button.</p>
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <Textarea
            value={listing.description}
            onChange={(e) => onChange({ ...listing, description: e.target.value })}
            placeholder={'West Indian cuisine with waterfront setting.\nHours vary / recommend calling ahead'}
            rows={3}
            className="mt-1"
            data-testid={`food-description-${id}`}
          />
          <p className="text-xs text-gray-500 mt-1">Line breaks are preserved in the pop-up.</p>
        </div>

        <div className="max-w-sm">
          <ImageDropUpload
            label="Logo"
            hint="transparent PNG works best - this is the click target"
            value={listing.logo_url}
            folder="food-drink/logos"
            aspect="aspect-[3/2]"
            testId={`food-logo-drop-${id}`}
            onUploaded={(url) => onChange({ ...listing, logo_url: url })}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const AdminFoodDrink = () => {
  const [listings, setListings] = useState([]);
  const [sections, setSections] = useState({});
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [activeTab, setActiveTab] = useState(CATEGORIES[0].key);
  const [savingSections, setSavingSections] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const bustPublicCache = () =>
    invalidateSettingsCache(`${process.env.REACT_APP_BACKEND_URL}/api/public/food-drink`);

  const load = async () => {
    try {
      const [listRes, secRes] = await Promise.all([
        axios.get(`${API}/food-drink/listings`, { headers: authHeaders() }),
        axios.get(`${API}/food-drink/sections`, { headers: authHeaders() }),
      ]);
      setListings(Array.isArray(listRes.data) ? listRes.data : []);
      setSections(secRes.data || {});
    } catch (error) {
      toast({
        title: 'Load failed',
        description: error.response?.data?.detail || 'Could not load Food & Drink listings.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveListing = async (listing) => {
    if (!listing.name?.trim()) {
      toast({ title: 'Name required', description: 'Give the listing a name first.', variant: 'destructive' });
      return;
    }
    setSavingId(listing.id || 'new');
    try {
      const payload = {
        name: listing.name,
        phone: listing.phone || '',
        description: listing.description || '',
        website_url: listing.website_url || '',
        logo_url: listing.logo_url || '',
        category: listing.category,
        section: listing.section,
        sort_order: Number(listing.sort_order) || 0,
        is_active: Boolean(listing.is_active),
      };
      if (listing.id) {
        await axios.put(`${API}/food-drink/listings/${listing.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/food-drink/listings`, payload, { headers: authHeaders() });
        setDraft(null);
      }
      bustPublicCache();
      toast({ title: 'Saved', description: `${payload.name} updated.` });
      await load();
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save the listing.',
        variant: 'destructive',
      });
    } finally {
      setSavingId(null);
    }
  };

  const deleteListing = async (listing) => {
    if (!listing.id) { setDraft(null); return; }
    if (!window.confirm(`Delete "${listing.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/food-drink/listings/${listing.id}`, { headers: authHeaders() });
      bustPublicCache();
      toast({ title: 'Deleted', description: `${listing.name} removed.` });
      await load();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Could not delete the listing.',
        variant: 'destructive',
      });
    }
  };

  const saveSections = async () => {
    setSavingSections(true);
    try {
      const { data } = await axios.put(`${API}/food-drink/sections`, sections, { headers: authHeaders() });
      setSections(data || {});
      bustPublicCache();
      toast({ title: 'Saved', description: 'Section headings updated.' });
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save the headings.',
        variant: 'destructive',
      });
    } finally {
      setSavingSections(false);
    }
  };

  const setSectionField = (categoryKey, field, value) => {
    setSections((prev) => ({
      ...prev,
      [categoryKey]: { ...(prev[categoryKey] || {}), [field]: value },
    }));
  };

  const visible = useMemo(
    () => listings.filter((l) => l.category === activeTab),
    [listings, activeTab],
  );
  const counts = useMemo(() => {
    const map = {};
    CATEGORIES.forEach((c) => { map[c.key] = listings.filter((l) => l.category === c.key).length; });
    return map;
  }, [listings]);

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="food-admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const cfg = sections[activeTab] || {};

  return (
    <div className="max-w-5xl" data-testid="admin-food-drink">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UtensilsCrossed className="w-7 h-7 text-blue-600" />
            Food &amp; Drink
          </h2>
          <p className="text-gray-500 mt-1">
            {listings.length} listing{listings.length === 1 ? '' : 's'} across 4 categories. Turn the
            component on or off with the <strong>food_drink_enabled</strong> feature flag.
          </p>
        </div>
        {!draft && (
          <Button
            onClick={() => { setDraft({ ...blankListing(), category: activeTab }); setOpenId('new'); }}
            data-testid="food-add-listing"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Listing
          </Button>
        )}
      </div>

      {/* category tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-3">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setActiveTab(c.key)}
            className={`px-3 py-2 text-sm font-bold rounded-t transition-colors ${
              activeTab === c.key
                ? 'bg-red-50 text-red-700 border-b-2 border-red-600'
                : 'text-gray-600 hover:text-red-700 hover:bg-gray-50'
            }`}
            data-testid={`food-tab-${c.key}`}
          >
            {c.label} <span className="text-xs font-normal">({counts[c.key] || 0})</span>
          </button>
        ))}
      </div>

      {/* section headings for the selected tab */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Section Headings</CardTitle>
              <CardDescription>
                Shown above each group on the public page. Leave a title blank to hide that section.
              </CardDescription>
            </div>
            <Button size="sm" onClick={saveSections} disabled={savingSections} data-testid="food-save-sections">
              {savingSections ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Headings
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Upper Title</Label>
            <Input
              value={cfg.upper_title || ''}
              onChange={(e) => setSectionField(activeTab, 'upper_title', e.target.value)}
              className="mt-1"
              data-testid="food-upper-title"
            />
            <Label className="mt-3 block">Upper Note</Label>
            <Textarea
              value={cfg.upper_note || ''}
              onChange={(e) => setSectionField(activeTab, 'upper_note', e.target.value)}
              rows={2}
              className="mt-1"
              data-testid="food-upper-note"
            />
          </div>
          <div>
            <Label>Lower Title</Label>
            <Input
              value={cfg.lower_title || ''}
              onChange={(e) => setSectionField(activeTab, 'lower_title', e.target.value)}
              className="mt-1"
              data-testid="food-lower-title"
            />
            <Label className="mt-3 block">Lower Note</Label>
            <Textarea
              value={cfg.lower_note || ''}
              onChange={(e) => setSectionField(activeTab, 'lower_note', e.target.value)}
              rows={2}
              className="mt-1"
              data-testid="food-lower-note"
            />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {draft && (
          <ListingEditor
            listing={draft}
            onChange={setDraft}
            onSave={() => saveListing(draft)}
            onDelete={() => setDraft(null)}
            saving={savingId === 'new'}
            isOpen={openId === 'new'}
            onToggle={() => setOpenId(openId === 'new' ? null : 'new')}
            sections={sections}
          />
        )}

        {visible.map((listing) => (
          <ListingEditor
            key={listing.id}
            listing={listing}
            onChange={(next) => setListings((prev) => prev.map((l) => (l.id === next.id ? next : l)))}
            onSave={() => saveListing(listing)}
            onDelete={() => deleteListing(listing)}
            saving={savingId === listing.id}
            isOpen={openId === listing.id}
            onToggle={() => setOpenId(openId === listing.id ? null : listing.id)}
            sections={sections}
          />
        ))}

        {!draft && visible.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              No listings in this category yet. Click <strong>Add Listing</strong> to create one.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminFoodDrink;
